import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Truck, User, Mail, Lock, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/driver/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone || null,
          invitation_token: invitationToken
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'inscription');
      }
      
      setSuccess(true);
      toast.success('Compte cree avec succes!');
      
      // Auto login
      setTimeout(() => {
        login(data.access_token, data.user);
        navigate('/dashboard');
      }, 1500);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Compte cree!</h1>
          <p className="text-slate-400">Redirection vers votre tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Header */}
      <div className="p-6 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <Truck className="w-7 h-7 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold text-white">TruckDriverPro</h1>
        </div>
        <p className="text-slate-400 text-sm">Application gratuite pour chauffeurs</p>
      </div>

      {/* Form */}
      <div className="flex-1 px-6 pb-8">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-semibold text-white mb-6">Creer un compte</h2>
          
          {invitationToken && (
            <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4 mb-6">
              <p className="text-amber-400 text-sm">
                Vous avez recu une invitation. Creez votre compte pour la consulter.
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Prenom</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-slate-900 border-slate-700 text-white"
                    placeholder="Jean"
                    data-testid="register-first-name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nom</label>
                <Input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="Tremblay"
                  data-testid="register-last-name"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-10 bg-slate-900 border-slate-700 text-white"
                  placeholder="jean@exemple.com"
                  data-testid="register-email"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Telephone (optionnel)</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10 bg-slate-900 border-slate-700 text-white"
                  placeholder="514-555-1234"
                  data-testid="register-phone"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="pl-10 bg-slate-900 border-slate-700 text-white"
                  placeholder="••••••••"
                  data-testid="register-password"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Confirmer le mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="pl-10 bg-slate-900 border-slate-700 text-white"
                  placeholder="••••••••"
                  data-testid="register-confirm-password"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold py-6 mt-6"
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Creation en cours...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Creer mon compte
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </form>
          
          <p className="text-center text-slate-400 mt-6">
            Deja un compte?{' '}
            <Link to="/login" className="text-amber-500 hover:text-amber-400">
              Se connecter
            </Link>
          </p>
          
          <p className="text-center text-slate-500 text-xs mt-8">
            En creant un compte, vous acceptez nos conditions d'utilisation
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
